import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError, from } from 'rxjs';
import { catchError, tap, switchMap } from 'rxjs/operators';
import { LOGO_FALLBACK_BASE64 } from '../../utils/logo-fallback';

interface CachedImage {
  base64: string;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class ImageCacheService {
  private memoryCache = new Map<string, string>();
  private readonly CACHE_PREFIX = 'img_cache_';
  private readonly CACHE_EXPIRY = 48 * 60 * 60 * 1000; // 48 hours
  private loadingPromises = new Map<string, Observable<string>>();

  constructor(private http: HttpClient) {
    // Pre-seed logo to guarantee instant and reliable logo loading across the entire app
    this.memoryCache.set('assets/images/logogen.jpg', LOGO_FALLBACK_BASE64);
    this.memoryCache.set('images/logogen.jpg', LOGO_FALLBACK_BASE64);
  }

  /**
   * Get image as base64 string with caching
   * @param imagePath - Path to the image (e.g., 'assets/images/logogen.jpg')
   * @returns Observable<string> - Base64 encoded image string
   */
  getImage(imagePath: string): Observable<string> {
    // Normalize image path
    const normalizedPath = this.normalizePath(imagePath);
    const cacheKey = this.getCacheKey(normalizedPath);

    // 1. Check memory cache first (fastest)
    if (this.memoryCache.has(normalizedPath)) {
      return of(this.memoryCache.get(normalizedPath)!);
    }

    // 2. Check localStorage (with expiry)
    const cached = this.getFromLocalStorage(cacheKey);
    if (cached) {
      // Store in memory cache for faster subsequent access
      this.memoryCache.set(normalizedPath, cached);
      return of(cached);
    }

    // 3. Check if already loading (prevent duplicate requests)
    if (this.loadingPromises.has(normalizedPath)) {
      return this.loadingPromises.get(normalizedPath)!;
    }

    // 4. Fetch from HTTP
    const loadingObservable = this.http
      .get(normalizedPath, { responseType: 'blob' })
      .pipe(
        switchMap((blob: Blob) => {
          // Convert Promise<string> to Observable<string>
          return from(this.blobToBase64(blob));
        }),
        tap((base64: string) => {
          // Store in both caches
          this.memoryCache.set(normalizedPath, base64);
          this.saveToLocalStorage(cacheKey, base64);
          // Remove from loading promises
          this.loadingPromises.delete(normalizedPath);
        }),
        catchError((error) => {
          // Remove from loading promises on error
          this.loadingPromises.delete(normalizedPath);
          console.error(`Error loading image ${normalizedPath}:`, error);
          if (normalizedPath.includes('logogen')) {
            this.memoryCache.set(normalizedPath, LOGO_FALLBACK_BASE64);
            return of(LOGO_FALLBACK_BASE64);
          }
          return throwError(() => error);
        })
      );

    // Store loading promise to prevent duplicate requests
    this.loadingPromises.set(normalizedPath, loadingObservable);

    return loadingObservable;
  }

  /**
   * Convert blob to base64 string
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result as string;
        resolve(base64data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Normalize image path to handle different path formats
   */
  private normalizePath(imagePath: string): string {
    // Remove leading ./ if present
    let normalized = imagePath.replace(/^\.\//, '');
    
    // Remove leading ../ sequences (relative paths)
    normalized = normalized.replace(/^(\.\.\/)+/, '');
    
    if (normalized.startsWith('/')) {
      return normalized;
    }
    
    // If it already starts with assets/, return as is (relative to base)
    // Angular will prepend the base href (/mail/) automatically
    if (normalized.startsWith('assets/')) {
      return normalized;
    }
    
    // Otherwise, prepend assets/ (relative path, Angular will prepend base href)
    return `assets/${normalized}`;
  }

  /**
   * Get cache key for localStorage
   */
  private getCacheKey(imagePath: string): string {
    return `${this.CACHE_PREFIX}${imagePath.replace(/\//g, '_')}`;
  }

  /**
   * Get image from localStorage with expiry check
   */
  private getFromLocalStorage(cacheKey: string): string | null {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) {
        return null;
      }

      const cachedImage: CachedImage = JSON.parse(cached);
      const now = Date.now();

      // Check if cache has expired
      if (now - cachedImage.timestamp > this.CACHE_EXPIRY) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return cachedImage.base64;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  }

  /**
   * Save image to localStorage
   */
  private saveToLocalStorage(cacheKey: string, base64: string): void {
    try {
      const cachedImage: CachedImage = {
        base64,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cachedImage));
    } catch (error) {
      // Handle quota exceeded error
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.clearOldCacheEntries();
        // Try again after clearing
        try {
          const cachedImage: CachedImage = {
            base64,
            timestamp: Date.now()
          };
          localStorage.setItem(cacheKey, JSON.stringify(cachedImage));
        } catch (retryError) {
          console.error('Failed to save image to localStorage after clearing:', retryError);
        }
      } else {
        console.error('Error saving to localStorage:', error);
      }
    }
  }

  /**
   * Clear old cache entries when quota is exceeded
   */
  private clearOldCacheEntries(): void {
    const keysToRemove: string[] = [];
    const now = Date.now();

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.CACHE_PREFIX)) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const cachedImage: CachedImage = JSON.parse(cached);
            // Remove entries older than cache expiry
            if (now - cachedImage.timestamp > this.CACHE_EXPIRY) {
              keysToRemove.push(key);
            }
          }
        } catch (error) {
          // If parsing fails, remove the key
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  /**
   * Clear cache for specific image or all images
   * @param imagePath - Optional image path. If not provided, clears all cached images
   */
  clearCache(imagePath?: string): void {
    if (imagePath) {
      const normalizedPath = this.normalizePath(imagePath);
      const cacheKey = this.getCacheKey(normalizedPath);
      
      // Clear from memory cache
      this.memoryCache.delete(normalizedPath);
      
      // Clear from localStorage
      localStorage.removeItem(cacheKey);
    } else {
      // Clear all cached images
      this.memoryCache.clear();
      
      // Clear all from localStorage
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CACHE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  }

  /**
   * Preload image into cache (useful for critical images)
   * @param imagePath - Path to the image
   */
  preloadImage(imagePath: string): Observable<string> {
    return this.getImage(imagePath);
  }
}
